import * as React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'
import { Check, ChevronDown, ChevronUp, Search, X } from 'lucide-react'

import { useDebounce } from '@/hooks/useDebounce'
import { cn } from '@/utils/common'

import { SEARCH_DEBOUNCE_DELAY_MS } from '../../utils/constants'

import { Button } from './button'
import { Input } from './input'
import { Spinner } from './spinner'

export type Option = {
  value: string
  label: string | React.ReactNode
  iconLeft?: React.ReactNode
  iconRight?: React.ReactNode
  disabled?: boolean
}

export type BaseSelectProps = {
  label?: string
  className?: string
  triggerClassName?: string
  options: Option[]
  placeholder?: React.ReactNode
  placeholderClassName?: string
  valueClassName?: string
  disabled?: boolean
  fullWidth?: boolean
  clearable?: boolean
  isHiddenBorder?: boolean
  searchable?: boolean
  searchPlaceholder?: string
  noMatchesText?: string
  maxHeight?: number
  contentClassName?: string
  noPadding?: boolean
  disableSorting?: boolean
  keepOpenOnSelect?: boolean
  align?: 'start' | 'center' | 'end'
  side?: 'top' | 'right' | 'bottom' | 'left'
  onSearchChange?: (query: string) => void
  isLoading?: boolean
}

export type SelectProps = BaseSelectProps & {
  value?: string
  defaultValue?: string
  onChange?: (value: string) => void
}

// ========== Primitive Components ==========
const SelectRoot = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
    showClearIcon?: boolean
    onClear?: (e: React.MouseEvent) => void
  }
>(({ className, children, showClearIcon, onClear, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background data-[placeholder]:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
      className
    )}
    {...props}
  >
    {children}
    <div className="flex items-center gap-1">
      {showClearIcon && onClear && (
        <Button
          type="button"
          variant="text"
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            onClear(e)
          }}
          onPointerDown={(e) => {
            e.stopPropagation()
          }}
          onMouseDown={(e) => {
            e.stopPropagation()
          }}
          className="flex items-center justify-center rounded-sm p-0.5 gap-0"
          aria-label="Clear selection"
          leadingIcon={<X className="size-3.5 text-gray-500" />}
        />
      )}
      <SelectPrimitive.Icon asChild>
        <ChevronDown className="w-4 h-4 opacity-50" />
      </SelectPrimitive.Icon>
    </div>
  </SelectPrimitive.Trigger>
))
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      'flex cursor-default items-center justify-center py-1',
      className
    )}
    {...props}
  >
    <ChevronUp className="w-4 h-4" />
  </SelectPrimitive.ScrollUpButton>
))
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      'flex cursor-default items-center justify-center py-1',
      className
    )}
    {...props}
  >
    <ChevronDown className="w-4 h-4" />
  </SelectPrimitive.ScrollDownButton>
))
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
    showScrollButtons?: boolean
    searchInput?: React.ReactNode
  }
>(
  (
    {
      className,
      children,
      position = 'popper',
      showScrollButtons = true,
      searchInput,
      ...props
    },
    ref
  ) => (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          'relative z-50 max-h-[--radix-select-content-available-height] min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-select-content-transform-origin]',
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          className
        )}
        position={position}
        {...props}
      >
        {searchInput}
        {showScrollButtons && <SelectScrollUpButton />}
        <SelectPrimitive.Viewport
          className={cn(
            position === 'popper' &&
              'h-[var(--radix-select-trigger-height)] w-full'
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        {showScrollButtons && <SelectScrollDownButton />}
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
)
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn('py-1.5 pl-8 text-sm font-semibold', className)}
    {...props}
  />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex gap-2 w-full cursor-pointer select-none items-center rounded-sm py-1.5 px-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 overflow-hidden',
      className
    )}
    {...props}
  >
    <div className="flex-1 overflow-hidden">{children}</div>
    <div className="flex h-3.5 w-3.5 items-center justify-center empty:hidden">
      <SelectPrimitive.ItemIndicator>
        <Check className="mt-2 size-4" />
      </SelectPrimitive.ItemIndicator>
    </div>
  </SelectPrimitive.Item>
))
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-muted', className)}
    {...props}
  />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

interface OptionItemProps {
  option: Option
  isHighlighted: boolean
  fullWidth: boolean
  itemRefs: React.MutableRefObject<Map<string, HTMLDivElement>>
}

const OptionItem = React.memo<OptionItemProps>(
  ({ option, isHighlighted, fullWidth, itemRefs }) => {
    return (
      <SelectItem
        value={option.value}
        disabled={option.disabled}
        className={cn(isHighlighted && 'bg-accent')}
      >
        <div
          ref={(el) => {
            if (el) {
              itemRefs.current.set(option.value, el)
            } else {
              itemRefs.current.delete(option.value)
            }
          }}
          className="flex items-center gap-4 text-sm"
        >
          {option.iconLeft && (
            <span className={cn(option.disabled && 'opacity-50')}>
              {option.iconLeft}
            </span>
          )}
          <SelectPrimitive.ItemText asChild>
            <span
              className={cn(
                'flex-1 break-words w-full text-[--Colors-Gray-light-900] ',
                fullWidth ? '' : 'max-w-[360px]'
              )}
            >
              {option.label}
            </span>
          </SelectPrimitive.ItemText>
          {option.iconRight && (
            <span className={cn(option.disabled && 'opacity-50')}>
              {option.iconRight}
            </span>
          )}
        </div>
      </SelectItem>
    )
  }
)
OptionItem.displayName = 'OptionItem'

interface OptionsListProps {
  filteredOptions: Option[]
  highlightedIndex: number
  isKeyboardNavigating: boolean
  searchable: boolean
  fullWidth: boolean
  noMatchesText: string
  itemRefs: React.MutableRefObject<Map<string, HTMLDivElement>>
  isLoading?: boolean
}

const OptionsList = React.memo<OptionsListProps>(
  ({
    filteredOptions,
    highlightedIndex,
    isKeyboardNavigating,
    searchable,
    fullWidth,
    noMatchesText,
    itemRefs,
    isLoading,
  }) => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center gap-2 py-6 text-sm text-center text-gray-500">
          <Spinner className="size-4 text-brand-600" />
          <span>Loading...</span>
        </div>
      )
    }

    if (!filteredOptions.length) {
      return (
        <div className="py-6 text-sm text-center text-gray-500">
          {noMatchesText}
        </div>
      )
    }

    return (
      <>
        {filteredOptions.map((opt, index) => {
          const isHighlighted =
            searchable && isKeyboardNavigating && index === highlightedIndex
          return (
            <OptionItem
              key={opt.value}
              option={opt}
              isHighlighted={isHighlighted}
              fullWidth={fullWidth}
              itemRefs={itemRefs}
            />
          )
        })}
      </>
    )
  }
)
OptionsList.displayName = 'OptionsList'

const handleArrowDown = (
  e: React.KeyboardEvent,
  filteredOptions: Option[],
  setHighlightedIndex: React.Dispatch<React.SetStateAction<number>>,
  setIsKeyboardNavigating: React.Dispatch<React.SetStateAction<boolean>>
) => {
  if (filteredOptions.length === 0) return
  e.preventDefault()
  setIsKeyboardNavigating(true)
  setHighlightedIndex((prev) =>
    prev < filteredOptions.length - 1 ? prev + 1 : prev
  )
}

const handleArrowUp = (
  e: React.KeyboardEvent,
  setHighlightedIndex: React.Dispatch<React.SetStateAction<number>>,
  setIsKeyboardNavigating: React.Dispatch<React.SetStateAction<boolean>>
) => {
  e.preventDefault()
  setIsKeyboardNavigating(true)
  setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev))
}

const handleEnterKey = (
  e: React.KeyboardEvent,
  filteredOptions: Option[],
  highlightedIndex: number,
  setValue: (value: string) => void
) => {
  if (filteredOptions.length === 0) return
  e.preventDefault()
  if (filteredOptions[highlightedIndex]) {
    setValue(filteredOptions[highlightedIndex].value)
  }
}

const handleEscapeKey = (
  e: React.KeyboardEvent,
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>,
  triggerRef: React.RefObject<HTMLButtonElement>
) => {
  e.preventDefault()
  setIsOpen(false)
  triggerRef.current?.focus()
}

const sortOptions = (options: Option[], disableSorting: boolean): Option[] => {
  if (disableSorting) return options

  const allOption = options.find((opt) => opt.value === 'all')
  const otherOptions = options
    .filter((opt) => opt.value !== 'all')
    .sort((a, b) => {
      const aLabel = typeof a.label === 'string' ? a.label : ''
      const bLabel = typeof b.label === 'string' ? b.label : ''
      return aLabel.localeCompare(bLabel)
    })
  return allOption ? [allOption, ...otherOptions] : otherOptions
}

const renderTriggerContent = (
  selectedOption: Option | undefined,
  placeholder: React.ReactNode,
  placeholderClassName: string | undefined,
  valueClassName: string | undefined
) => {
  if (!selectedOption) {
    return typeof placeholder === 'string' ? (
      <span className={cn('flex-1 truncate', placeholderClassName)}>
        {placeholder}
      </span>
    ) : (
      placeholder
    )
  }

  return (
    <>
      {selectedOption.iconLeft && (
        <span className="flex-shrink-0">{selectedOption.iconLeft}</span>
      )}
      <span className={cn('flex-1 truncate', valueClassName)}>
        {selectedOption.label}
      </span>
      {selectedOption.iconRight && (
        <span className="flex-shrink-0">{selectedOption.iconRight}</span>
      )}
    </>
  )
}

const renderSearchInput = (
  searchInputRef: React.RefObject<HTMLInputElement>,
  searchQuery: string,
  handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void,
  handleClearSearch: () => void,
  searchPlaceholder: string
) => (
  <div className="sticky top-0 z-10 bg-white border-b border-[--Colors-Gray-light-300] p-2">
    <Input
      ref={searchInputRef}
      type="text"
      value={searchQuery}
      onChange={handleSearchChange}
      onKeyDown={handleKeyDown}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      placeholder={searchPlaceholder}
      role="searchbox"
      aria-label="Search options"
      aria-controls="options-list"
      leadingIcon={<Search className="w-4 h-4 text-gray-400" />}
      trailingIcon={
        searchQuery ? <X className="w-3.5 h-3.5 text-gray-500" /> : undefined
      }
      onTrailingIconClick={searchQuery ? handleClearSearch : undefined}
      size="sm"
      className="text-sm"
    />
  </div>
)

const getContentStyle = (maxHeight: number, fullWidth: boolean) => ({
  maxHeight: `${maxHeight}px`,
  ...(fullWidth ? { width: 'var(--radix-select-trigger-width)' } : {}),
})

export const Select: React.FC<SelectProps> = ({
  label,
  className,
  triggerClassName,
  options,
  value,
  defaultValue,
  onChange,
  placeholder = 'Select an option',
  placeholderClassName,
  valueClassName,
  disabled = false,
  fullWidth = false,
  clearable = false,
  isHiddenBorder = false,
  searchable = false,
  searchPlaceholder = 'Search...',
  noMatchesText = 'No matches found.',
  maxHeight = 400,
  contentClassName,
  noPadding = false,
  disableSorting = false,
  keepOpenOnSelect = false,
  align = 'start',
  side = 'bottom',
  onSearchChange,
  isLoading = false,
}) => {
  const controlled = value !== undefined
  const [inner, setInner] = useState<string>(defaultValue || '')
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebounce(
    searchQuery,
    SEARCH_DEBOUNCE_DELAY_MS
  )
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const [isKeyboardNavigating, setIsKeyboardNavigating] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const isSelectingRef = useRef<boolean>(false)
  const selectedOptionCache = useRef<Option | null>(null)

  useEffect(() => {
    if (!controlled) setInner(defaultValue || '')
  }, [controlled, defaultValue])

  useEffect(() => {
    if (onSearchChange) {
      onSearchChange(debouncedSearchQuery)
    }
  }, [debouncedSearchQuery, onSearchChange])

  const selectedValue = controlled ? value : inner || ''

  const setValue = useCallback(
    (v: string) => {
      // Mark that we're selecting an item (used to prevent closing if keepOpenOnSelect is true)
      if (keepOpenOnSelect) {
        isSelectingRef.current = true
      }
      if (!v) {
        selectedOptionCache.current = null
      }
      if (!controlled) setInner(v)
      onChange?.(v)
      setSearchQuery('')
    },
    [controlled, onChange, keepOpenOnSelect]
  )

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      setValue('')
    },
    [setValue]
  )

  const handleOpenChange = useCallback(
    (open: boolean) => {
      const shouldPreventClose =
        !open && keepOpenOnSelect && isSelectingRef.current
      if (shouldPreventClose) {
        isSelectingRef.current = false
        return
      }

      setIsOpen(open)
      if (!open) return

      setSearchQuery('')
      setHighlightedIndex(0)
      setIsKeyboardNavigating(false)
      if (searchable) {
        setTimeout(() => searchInputRef.current?.focus(), 0)
      }
    },
    [searchable, keepOpenOnSelect]
  )

  const validOptions = options.filter((opt) => opt.value !== '')

  const sortedOptions = useMemo(
    () => sortOptions(validOptions, disableSorting),
    [validOptions, disableSorting]
  )

  const filteredOptions = useMemo(
    () =>
      searchable
        ? sortedOptions.filter((opt) => {
            const labelText = typeof opt.label === 'string' ? opt.label : ''
            return labelText
              .toLowerCase()
              .includes(debouncedSearchQuery.toLowerCase())
          })
        : sortedOptions,
    [searchable, sortedOptions, debouncedSearchQuery]
  )

  const selectedOption = useMemo(() => {
    const found = sortedOptions.find((opt) => opt.value === selectedValue)
    if (found) {
      selectedOptionCache.current = found
      return found
    }
    // Fallback: use cached option if value matches but option is not in current list
    if (selectedOptionCache.current?.value === selectedValue) {
      return selectedOptionCache.current
    }
    return undefined
  }, [sortedOptions, selectedValue])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }

  const handleClearSearch = () => {
    if (!searchQuery) return
    setSearchQuery('')
    searchInputRef.current?.focus()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation()
    if (!searchable) return

    switch (e.key) {
      case 'ArrowDown':
        handleArrowDown(
          e,
          filteredOptions,
          setHighlightedIndex,
          setIsKeyboardNavigating
        )
        break
      case 'ArrowUp':
        handleArrowUp(e, setHighlightedIndex, setIsKeyboardNavigating)
        break
      case 'Enter':
        handleEnterKey(e, filteredOptions, highlightedIndex, setValue)
        break
      case 'Escape':
        handleEscapeKey(e, setIsOpen, triggerRef)
        break
    }
  }

  useEffect(() => {
    const shouldScrollIntoView = isOpen && searchable && isKeyboardNavigating
    if (!shouldScrollIntoView) return

    const highlightedElement = itemRefs.current.get(
      filteredOptions[highlightedIndex]?.value
    )
    highlightedElement?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    })
  }, [
    highlightedIndex,
    isOpen,
    searchable,
    isKeyboardNavigating,
    filteredOptions,
  ])

  return (
    <div className={cn(className)}>
      {label && (
        <label className="block mb-1 text-sm font-medium text-[--Colors-Gray-light-700]">
          {label}
        </label>
      )}

      <SelectRoot
        value={selectedValue}
        onValueChange={setValue}
        disabled={disabled}
        open={isOpen}
        onOpenChange={handleOpenChange}
      >
        <SelectTrigger
          ref={triggerRef}
          showClearIcon={clearable && !!selectedValue && !disabled}
          onClear={handleClear}
          className={cn(
            'radix-select-trigger',
            'flex min-h-10 items-center gap-2 rounded-lg border border-solid border-[--Colors-Gray-light-300] bg-white text-sm transition-colors',
            'hover:bg-gray-50',
            isHiddenBorder ? 'border-none hover:border-none' : '',
            'hover:border-blue-400',
            'focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500',
            disabled
              ? 'cursor-not-allowed opacity-60 text-gray-600 font-md'
              : 'cursor-pointer',
            disabled && !isHiddenBorder
              ? 'border border-solid border-gray-400'
              : '',
            fullWidth ? 'w-full' : 'max-w-[320px]',
            'text-left',
            noPadding ? 'p-0' : 'px-3 py-2',
            triggerClassName
          )}
        >
          <div className="flex items-center flex-1 gap-2 overflow-hidden">
            {renderTriggerContent(
              selectedOption,
              placeholder,
              placeholderClassName,
              valueClassName
            )}
          </div>
        </SelectTrigger>

        <SelectContent
          className={cn(
            'select-scrollbar',
            'z-50 overflow-hidden',
            'rounded-lg border border-solid border-[--Colors-Gray-light-300] bg-white',
            !fullWidth && 'max-w-[320px]',
            fullWidth
              ? 'w-[var(--radix-select-trigger-width)]'
              : 'max-w-[320px]',
            contentClassName
          )}
          style={getContentStyle(maxHeight, fullWidth)}
          position="popper"
          align={align}
          showScrollButtons={filteredOptions.length > 5}
          onCloseAutoFocus={(event) => {
            event.preventDefault()
            triggerRef.current?.blur()
          }}
          searchInput={
            searchable
              ? renderSearchInput(
                  searchInputRef,
                  searchQuery,
                  handleSearchChange,
                  handleKeyDown,
                  handleClearSearch,
                  searchPlaceholder
                )
              : undefined
          }
          side={side}
        >
          <OptionsList
            filteredOptions={filteredOptions}
            highlightedIndex={highlightedIndex}
            isKeyboardNavigating={isKeyboardNavigating}
            searchable={searchable}
            fullWidth={fullWidth}
            noMatchesText={noMatchesText}
            itemRefs={itemRefs}
            isLoading={isLoading}
          />
        </SelectContent>
      </SelectRoot>
    </div>
  )
}

export {
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectRoot,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
}
